/**
 * Cabinet toast queue (sc-3122).
 *
 * Tiny, canvas-native notification surface for the cabinet. Slides in
 * from the bottom-right of the canvas, holds for a short time, fades
 * out. Multiple toasts stack vertically.
 *
 * Why canvas, not DOM:
 *   The cabinet renders EVERYTHING inside the bezel — selector,
 *   press-start, pause overlay, initials overlay, every cartridge.
 *   A DOM toast would float outside that frame and break the
 *   "everything happens on the arcade screen" metaphor. The toast
 *   paints directly into the 2D context like the pause overlay does.
 *
 * Why a singleton:
 *   The push site (a network error in submitScore) doesn't have a
 *   reference to the cabinet's rAF loop. A module-level queue lets
 *   `push()` be called from anywhere and `renderInto()` to be wired
 *   into each rAF loop (runtime, selector) with one line.
 *
 * What this is NOT (yet):
 *   - No retry affordance. The toast is informational. sc-???? will
 *     add a "TAP TO RETRY" interaction.
 *   - No accessibility hook. DOM-based aria-live regions would be the
 *     right answer for screen readers; canvas text isn't reachable.
 *     Filed mentally as followup.
 *   - No sound. Hooking ctx.audio here would couple toast to the
 *     audio bus; current scope keeps it visual-only.
 */

const TOAST_HOLD_MS = 3000;
const TOAST_FADE_MS = 400;
const TOAST_SLIDE_MS = 220;
const TOAST_LIFE_MS = TOAST_HOLD_MS + TOAST_FADE_MS;

const TOAST_W = 200;
const TOAST_H = 28;
const TOAST_MARGIN = 8;
const TOAST_GAP = 4;

const COLOR_ERROR_BG = 'rgba(40, 6, 10, 0.92)';
const COLOR_ERROR_BORDER = '#ff3030';
const COLOR_ERROR_TEXT = '#ffcc88';
const COLOR_INFO_BG = 'rgba(6, 16, 24, 0.92)';
const COLOR_INFO_BORDER = '#3fb8e6';
const COLOR_INFO_TEXT = '#cce8f5';

export type ToastVariant = 'error' | 'info';

interface ToastEntry {
  id: number;
  message: string;
  variant: ToastVariant;
  /** ms timestamp when the toast was pushed. Used to compute
   *  slide-in / hold / fade-out phases. */
  pushedAt: number;
}

const queue: ToastEntry[] = [];
let nextId = 1;
/** Optional clock override for tests — `Date.now()` by default. */
let clock: () => number = () => Date.now();

export interface PushToastOptions {
  variant?: ToastVariant;
}

/** Enqueue a toast. Returns the entry id (useful if a future caller
 *  needs to dismiss programmatically). */
export function pushToast(message: string, opts: PushToastOptions = {}): number {
  const id = nextId++;
  queue.push({
    id,
    message,
    variant: opts.variant ?? 'info',
    pushedAt: clock(),
  });
  return id;
}

/** Drop expired toasts. Pure side-effect on the queue. Callers don't
 *  need to invoke this manually — `renderInto()` calls it. Exposed
 *  for tests. */
export function expireToasts(now: number = clock()): void {
  for (let i = queue.length - 1; i >= 0; i--) {
    const entry = queue[i];
    if (!entry) continue;
    if (now - entry.pushedAt > TOAST_LIFE_MS) queue.splice(i, 1);
  }
}

/** Number of live toasts. Exposed for tests. */
export function activeToastCount(): number {
  expireToasts();
  return queue.length;
}

/** Clear all toasts. Exposed for tests + a potential future "mute"
 *  affordance. */
export function clearToasts(): void {
  queue.length = 0;
}

/** Test-only clock injection. The runtime always reads `Date.now()`. */
export function __setToastClockForTests(fn: () => number): void {
  clock = fn;
}

/** Reset the module to a clean slate. Test-only — the prod runtime
 *  never needs this since the queue is naturally bounded by TTL. */
export function __resetToastsForTests(): void {
  queue.length = 0;
  nextId = 1;
  clock = (): number => Date.now();
}

/**
 * Paint all live toasts into the 2D context. Call this once per frame
 * AFTER the cartridge / overlay paint so toasts overlay everything
 * else.
 *
 * Toasts stack from the bottom-right corner of the canvas, growing
 * upward. Each toast slides in for the first ~220ms, holds for ~3s,
 * then fades out over ~400ms.
 *
 * Determinism: this method mutates only the toast queue's internal
 * state (expiry). It does not touch PRNG or any cartridge state.
 */
export function renderInto(ctx: CanvasRenderingContext2D): void {
  const now = clock();
  expireToasts(now);
  if (queue.length === 0) return;

  const cw = ctx.canvas.width;
  const ch = ctx.canvas.height;

  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 10px ui-monospace, monospace';

  // Stack toasts from bottom-up so the newest is at the bottom and
  // older ones sit on top of it. This matches the typical "most
  // recent message is closest" reading order.
  let y = ch - TOAST_MARGIN - TOAST_H;
  // Render newest first (last in the queue), older above.
  for (let i = queue.length - 1; i >= 0; i--) {
    const entry = queue[i];
    if (!entry) continue;
    const age = now - entry.pushedAt;

    // Phases: slide-in (0..TOAST_SLIDE_MS), hold, fade-out
    // (TOAST_HOLD_MS..TOAST_LIFE_MS).
    let slideFrac = Math.min(1, age / TOAST_SLIDE_MS);
    let alpha = 1;
    if (age > TOAST_HOLD_MS) {
      alpha = Math.max(0, 1 - (age - TOAST_HOLD_MS) / TOAST_FADE_MS);
    }
    // Ease-out for the slide.
    slideFrac = 1 - (1 - slideFrac) * (1 - slideFrac);
    const x = cw - TOAST_MARGIN - TOAST_W * slideFrac;

    const isError = entry.variant === 'error';
    ctx.globalAlpha = alpha;
    ctx.fillStyle = isError ? COLOR_ERROR_BG : COLOR_INFO_BG;
    ctx.fillRect(x, y, TOAST_W, TOAST_H);
    ctx.strokeStyle = isError ? COLOR_ERROR_BORDER : COLOR_INFO_BORDER;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, TOAST_W - 1, TOAST_H - 1);

    // Variant glyph in the left padding.
    ctx.fillStyle = isError ? COLOR_ERROR_BORDER : COLOR_INFO_BORDER;
    ctx.fillRect(x + 6, y + TOAST_H / 2 - 3, 6, 6);

    ctx.fillStyle = isError ? COLOR_ERROR_TEXT : COLOR_INFO_TEXT;
    ctx.fillText(entry.message, x + 18, y + TOAST_H / 2);

    y -= TOAST_H + TOAST_GAP;
    // Stop early if the next toast would extend above the radar/HUD
    // area — keep the toast stack inside the canvas.
    if (y < 0) break;
  }

  ctx.globalAlpha = 1;
  ctx.restore();
}
